"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useId, useRef, useState, useTransition, type KeyboardEvent as ReactTaste } from "react";
import { createPortal } from "react-dom";
import { Alert } from "../../bausteine/alert";
import { Badge } from "../../bausteine/badge";
import { MENUE_EINTRAG } from "../../bausteine/dropdown-menue";
import { initialen } from "../../bausteine/formular/auswahl-konfiguration";
import { Kontrollkaestchen, Schalter } from "../../bausteine/formular/schalter";
import { Suchfeld } from "../../bausteine/formular/textfeld";
import { Knopf } from "../../bausteine/knopf";
import { SparklesSymbol } from "../konto/symbole";
import {
  DEV_KI_VORSCHAU_SATZ,
  DEV_SICHT_SATZ,
  DEV_FEHLSCHLAG_ERZEUGEN,
  DEV_FEHLSCHLAG_LISTE,
  DEV_FEHLSCHLAG_SCHALTER,
  DEV_FEHLSCHLAG_WECHSEL,
  filterePersonen,
  gespeichertAusSicht,
  istMenueKuerzel,
  personAnzeige,
  pillenText,
  satzFuerFehler,
  type DevMenuePerson,
  type DevSicht,
} from "./dev-menue-logik";

/**
 * Dev-Fenster „Entwicklung“ in der Kopfzeile (Musterkatalog M-057, Design-Guide `dev-menue.md`): die Umgebungs-Pille
 * (DEV, auf Stage STAGE) ist der Auslöser einer Seitenleiste rechts (Kandidat B, Betreiber 13.10.2026 „W108 Empfehlung“;
 * 760 px, volle Höhe, kein Abdunkeln, die Seite links bleibt sichtbar und bedienbar; mobil Vollbild). Kopf mit Umgebung,
 * Build und Zeitzone, links eine Reiterspalte in Gruppen (Sitzung · Prüfen · Rückmeldung · Plattform; nur Gruppen mit
 * vorhandenen Reitern), rechts der Inhalt mit eigenem Scroll, Fuß mit Tastenkürzel (Strg+Umschalt+D) und „Alle Dev-Seiten“.
 * Zustand (offen, letzter Reiter) liegt je Browser im localStorage, nie auf dem Server. Abweichungen: AW-086.
 *
 * Der Baustein prüft nichts und lädt nichts selbst: ob er überhaupt ausgeliefert wird (nie in Prod; auf Stage nur
 * admin/aihub_team), entscheidet der Server mit `pruefeDevWerkzeugeZugang`, dieselbe Prüfung wie jede Action (r109, r110).
 * Alle schreibenden Wege sind Server-Actions, die der Aufrufer hereinreicht und die selbst prüfen; der Reiter Plattform
 * kommt nur auf dev/verify (`plattform` fehlt sonst). Meine Sicht: Entwicklersicht · Nutzersicht (gespeichert, reine Anzeige;
 * in der Nutzersicht schrumpft das Menü auf den Umschalter), KI-Vorschau „aus“ (Server-Signal per Action, nur dev/verify),
 * Marker und Raster (localStorage).
 *
 * Bei laufender Impersonation trägt die Pille den Namen der dargestellten Person.
 */

const KEY_AI_MARKER_AN = "hh_devtools_ai_markers_on";
const KEY_RASTER_AN = "hh_devtools_grid_on";

// Auslöser: Pille mit Symbol und Chevron (Abweichung AW-072 (1): das Abzeichen des Katalogs ist nicht bedienbar, der Auslöser ist ein Knopf in Abzeichen-Optik).
const PILLE_PRIMAER =
  "inline-flex items-center gap-x-1.5 py-1 ps-2 pe-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 hover:bg-primary-200 focus:outline-hidden focus:ring-2 focus:ring-primary";
const PILLE_WARNUNG =
  "inline-flex items-center gap-x-1.5 py-1 ps-2 pe-1.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:outline-hidden focus:ring-2 focus:ring-primary";
// Seitenleiste rechts (Abweichung AW-086): mobil Vollbild, ab sm 760 px (w-190) an den rechten Rand, volle Höhe, höchstens 11/12 der Breite.
// Kein Backdrop: die Seite bleibt bedienbar. z-70 liegt über der Kopfzeile (z-50), unter den Modalen (z-80).
const FENSTER_FLAECHE =
  "fixed inset-0 sm:start-auto sm:w-190 sm:max-w-11/12 z-70 flex flex-col bg-overlay border-s border-overlay-line shadow-xl";
// Nutzersicht: das Fenster schrumpft auf eine kleine Karte mit dem Umschalter (kein Kopf, keine Reiter).
const FENSTER_KARTE =
  "fixed top-16 inset-x-2 sm:inset-x-auto sm:end-4 sm:w-100 z-70 bg-overlay border border-overlay-line rounded-xl shadow-xl px-4 py-3";
const REITER_SPALTE =
  "flex sm:flex-col shrink-0 gap-x-1 overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto px-2 sm:pb-3 border-b sm:border-b-0 sm:border-e border-line-2 sm:w-50";
const REITER_GRUPPE_KOPF = "hidden sm:block px-3 pt-4 pb-1 text-xs font-medium uppercase text-muted-foreground-1";
const REITER_AKTIV =
  "inline-flex items-center gap-x-3 px-3 py-2 text-sm text-start text-foreground whitespace-nowrap border-b-2 border-primary sm:border-b-0 sm:w-full sm:rounded-lg sm:bg-muted focus:outline-hidden focus-visible:bg-muted-focus";
const REITER_INAKTIV =
  "inline-flex items-center gap-x-3 px-3 py-2 text-sm text-start text-muted-foreground-1 whitespace-nowrap border-b-2 border-transparent sm:border-b-0 sm:w-full sm:rounded-lg hover:text-foreground sm:hover:bg-muted-hover focus:outline-hidden focus-visible:bg-muted-focus";
// Gespeichert je Browser (kein Server-Zustand): Fenster offen, zuletzt gewählter Reiter.
const KEY_FENSTER_OFFEN = "hh_devtools_fenster_offen";
const KEY_FENSTER_REITER = "hh_devtools_fenster_reiter";
// Feste, vollständige Ketten (nie zusammengesetzt, R8).
const TEXT_GEDAEMPFT = "text-xs text-muted-foreground-1";
const TEXT_GEDAEMPFT_ABSTAND = "text-xs text-muted-foreground-1 mt-1.5";
const TEXT_GEDAEMPFT_ZEILE = "block truncate text-xs text-muted-foreground-1";
const KOPF_ZEILE = "flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground-1";

function Svg({ klasse = "shrink-0 size-3.5", children }: { klasse?: string; children: React.ReactNode }) {
  return (
    <svg className={klasse} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
const Kolben = () => (
  <Svg>
    <path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2" />
    <path d="M8.5 2h7" />
    <path d="M7 16h10" />
  </Svg>
);
const ChevronUnten = () => (
  <Svg>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);
const ChevronRechts = () => (
  <Svg klasse="shrink-0 size-4 text-muted-foreground-1">
    <path d="m9 18 6-6-6-6" />
  </Svg>
);
const Extern = () => (
  <Svg>
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </Svg>
);
const NutzerPlus = () => (
  <Svg klasse="shrink-0 size-4">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" x2="19" y1="8" y2="14" />
    <line x1="22" x2="16" y1="11" y2="11" />
  </Svg>
);
const Zurueck = () => (
  <Svg klasse="shrink-0 size-4">
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </Svg>
);
const Ablauf = () => (
  <Svg klasse="shrink-0 size-4">
    <rect width="8" height="8" x="3" y="3" rx="2" />
    <path d="M7 11v4a2 2 0 0 0 2 2h4" />
    <rect width="8" height="8" x="13" y="13" rx="2" />
  </Svg>
);
const Datenbank = () => (
  <Svg klasse="shrink-0 size-4">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M3 5v14a9 3 0 0 0 18 0V5" />
    <path d="M3 12a9 3 0 0 0 18 0" />
  </Svg>
);

const Auge = () => (
  <Svg klasse="shrink-0 size-4">
    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);
const Personen = () => (
  <Svg klasse="shrink-0 size-4">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);
const Server = () => (
  <Svg klasse="shrink-0 size-4">
    <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
    <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
    <line x1="6" x2="6.01" y1="6" y2="6" />
    <line x1="6" x2="6.01" y1="18" y2="18" />
  </Svg>
);
const Schliessen = () => (
  <Svg klasse="shrink-0 size-4">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Svg>
);

/** Gültigkeit der Einstellungen: nur diese Sitzung (grau) oder für alle (gelb). */
function Geltung({ art }: { art: "sitzung" | "alle" }) {
  return art === "sitzung" ? <Badge ton="gedaempft">nur diese Sitzung</Badge> : <Badge ton="warnung">wirkt für alle</Badge>;
}

function Abschnittskopf({ titel, geltung }: { titel: string; geltung: "sitzung" | "alle" }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-1">
      <span className="font-semibold text-xs uppercase text-foreground">{titel}</span>
      <Geltung art={geltung} />
    </div>
  );
}

/** Zeile mit Beschriftung links und Schalter rechts (Block account-menu-with-theme-switch); KI-Zeilen tragen `ai-gated`. */
function SchalterZeile({
  titel,
  hinweis,
  an,
  beiUmschalten,
  ki = false,
  gesperrt = false,
  id,
}: {
  titel: string;
  hinweis: string;
  an: boolean;
  beiUmschalten: () => void;
  ki?: boolean;
  gesperrt?: boolean;
  id: string;
}) {
  return (
    <div className={ki ? "flex justify-between items-start gap-3 py-2 ai-gated" : "flex justify-between items-start gap-3 py-2"}>
      <div className="min-w-0">
        <span className="flex items-center gap-x-1.5 text-sm text-foreground">
          {ki ? (
            <span className="text-ki ai-mark" data-ki="gated">
              <SparklesSymbol klasse="shrink-0 size-3.5" />
            </span>
          ) : null}
          {titel}
        </span>
        <p className={TEXT_GEDAEMPFT}>{hinweis}</p>
      </div>
      <Schalter id={id} layout="zelle" label={titel} checked={an} disabled={gesperrt} onChange={beiUmschalten} />
    </div>
  );
}

// ------------------------------------------------------------------------------------------
// Meine Sicht

/** Umschalter Entwicklersicht · Nutzersicht (W100 a): derselbe Block im vollen Menü und im geschrumpften Menü der Nutzersicht. */
function SichtUmschalter({
  sicht,
  wechsle,
  wartet,
  satz,
}: {
  sicht: DevSicht;
  wechsle: (neu: DevSicht) => void;
  wartet: boolean;
  satz: string | null;
}) {
  return (
    <>
      <div className="py-2">
        <span className="block text-sm text-foreground mb-1.5">Sicht</span>
        <div className="flex gap-x-2" role="group" aria-label="Sicht">
          <Knopf groesse="xs" variante={sicht === "entwickler" ? "primaer" : "sekundaer"} ariaPressed={sicht === "entwickler"} onClick={() => wechsle("entwickler")} disabled={wartet}>
            Entwicklersicht
          </Knopf>
          <Knopf groesse="xs" variante={sicht === "nutzer" ? "primaer" : "sekundaer"} ariaPressed={sicht === "nutzer"} onClick={() => wechsle("nutzer")} disabled={wartet}>
            Nutzersicht
          </Knopf>
        </div>
        <p className={TEXT_GEDAEMPFT_ABSTAND}>{DEV_SICHT_SATZ} Wird für dich gespeichert.</p>
      </div>
      {satz ? <Alert ton="warnung">{satz}</Alert> : null}
    </>
  );
}

function MeineSichtInhalt({
  sicht,
  wechsle,
  wartet,
  satz,
  ansicht,
  kiVorschau,
  aiEnabled,
}: {
  sicht: DevSicht;
  wechsle: (neu: DevSicht) => void;
  wartet: boolean;
  satz: string | null;
  ansicht: Ansicht;
  kiVorschau: DevMenueKiVorschau | null;
  aiEnabled: boolean;
}) {
  return (
    <div>
      <Abschnittskopf titel="Meine Sicht" geltung="sitzung" />
      <SichtUmschalter sicht={sicht} wechsle={wechsle} wartet={wartet} satz={satz} />
      {kiVorschau ? (
        <SchalterZeile
          id="dev-menue-ki-vorschau"
          ki
          titel="KI-Vorschau „aus“"
          hinweis={aiEnabled ? DEV_KI_VORSCHAU_SATZ : "Die KI ist im Betrieb ausgeschaltet, die Seiten zeigen sich ohnehin ohne KI."}
          an={kiVorschau.aus}
          gesperrt={!aiEnabled}
          beiUmschalten={kiVorschau.umschalten}
        />
      ) : null}
      <SchalterZeile
        id="dev-menue-ki-marker"
        ki
        titel="KI-Marker"
        hinweis="Umrandet KI-Bereiche gestrichelt, hebt KI-Auslöser mit Ring hervor."
        an={ansicht.aiMarkerAn}
        beiUmschalten={ansicht.schalteAiMarker}
      />
      <SchalterZeile id="dev-menue-raster" titel="Raster" hinweis="Umriss um jedes Element der Seite." an={ansicht.rasterAn} beiUmschalten={ansicht.schalteRaster} />
    </div>
  );
}

// ------------------------------------------------------------------------------------------
// Ansicht als

export interface DevMenuePersonen {
  /** Server-Action `listImpersonatableUsers` (prüft selbst Sitzung, Umgebung und Rolle). */
  laden: () => Promise<DevMenuePerson[]>;
  /** Server-Action `startImpersonation` (prüft selbst, erneut `canImpersonate`). */
  wechseln: (userId: string) => Promise<void>;
  /** Server-Action `stopImpersonation`. */
  beenden: () => Promise<void>;
}

export interface DevMenueTestNutzer {
  /** Wählbare Rollen (ohne Administrator, das lehnt die Action ohnehin ab). */
  rollen: readonly { key: string; label: string }[];
  /** Server-Action `generateTestUser` (Sitzung, Umgebung und Rolle prüft sie selbst). */
  erzeugen: (rollen: string[]) => Promise<{ id: string }>;
}

function AnsichtAlsInhalt({
  aktiv,
  personen,
  testNutzer,
  impersoniertAls,
  neuLaden,
}: {
  aktiv: boolean;
  personen: DevMenuePersonen;
  testNutzer: DevMenueTestNutzer;
  impersoniertAls: string | null;
  neuLaden: () => void;
}) {
  const [liste, setzeListe] = useState<DevMenuePerson[] | null>(null);
  const [listenSatz, setzeListenSatz] = useState<string | null>(null);
  const [satz, setzeSatz] = useState<string | null>(null);
  const [suche, setzeSuche] = useState("");
  const [wartet, starte] = useTransition();
  const [erzeugenOffen, setzeErzeugenOffen] = useState(false);  const [gewaehlt, setzeGewaehlt] = useState<string[]>([]);
  const geladen = useRef(false);

  // Die Liste wird erst geladen, wenn der Reiter zum ersten Mal sichtbar ist (kein Abruf beim bloßen Öffnen des Fensters).
  useEffect(() => {
    if (!aktiv || geladen.current) return;
    geladen.current = true;
    personen
      .laden()
      .then((rows) => setzeListe(rows))
      .catch((e: unknown) => setzeListenSatz(satzFuerFehler(e, DEV_FEHLSCHLAG_LISTE)));
  }, [aktiv, personen]);

  function wechsle(p: DevMenuePerson) {
    if (!p.allowed || wartet) return;
    setzeSatz(null);
    starte(async () => {
      try {
        await personen.wechseln(p.id);
        neuLaden();
      } catch (e) {
        setzeSatz(satzFuerFehler(e, DEV_FEHLSCHLAG_WECHSEL));
      }
    });
  }

  function beende() {
    if (wartet) return;
    setzeSatz(null);
    starte(async () => {
      try {
        await personen.beenden();
        neuLaden();
      } catch (e) {
        setzeSatz(satzFuerFehler(e, DEV_FEHLSCHLAG_WECHSEL));
      }
    });
  }

  function erzeuge() {
    if (gewaehlt.length === 0 || wartet) return;
    setzeSatz(null);
    starte(async () => {
      try {
        const neu = await testNutzer.erzeugen(gewaehlt);
        await personen.wechseln(neu.id);
        neuLaden();
      } catch (e) {
        setzeSatz(satzFuerFehler(e, DEV_FEHLSCHLAG_ERZEUGEN));
      }
    });
  }

  const sichtbar = liste ? filterePersonen(liste, suche) : [];

  return (
    <div>
      <Abschnittskopf titel="Ansicht als" geltung="sitzung" />
      {impersoniertAls ? (
        <div className="py-2">
          <Knopf groesse="xs" variante="sekundaer" symbol={<Zurueck />} onClick={beende} disabled={wartet}>
            Zurück zu mir
          </Knopf>
        </div>
      ) : null}
      <div className="mb-2">
        <Suchfeld id="dev-menue-personen-suche" label="Person suchen" placeholder="Person suchen …" autoComplete="off" value={suche} onChange={(e) => setzeSuche(e.target.value)} />
      </div>
      {satz ? (
        <div className="mb-2">
          <Alert ton="warnung">{satz}</Alert>
        </div>
      ) : null}
      {listenSatz ? <Alert ton="warnung">{listenSatz}</Alert> : null}
      {!listenSatz && liste === null ? (
        <p className="py-2 text-sm text-muted-foreground-1" role="status">
          Lade Personen …
        </p>
      ) : null}
      {liste !== null && liste.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground-1" role="status">
          Keine Personen zum Wechseln.
        </p>
      ) : null}
      {liste !== null && liste.length > 0 && sichtbar.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground-1" role="status">
          Keine Person gefunden.
        </p>
      ) : null}
      <div className="space-y-0.5">
        {sichtbar.map((p) => (
          <button key={p.id} type="button" className={MENUE_EINTRAG} disabled={!p.allowed || wartet} onClick={() => wechsle(p)}>
            <span className="flex shrink-0 justify-center items-center size-7 rounded-full bg-surface text-xs font-medium text-foreground" aria-hidden="true">
              {initialen(personAnzeige(p))}
            </span>
            <span className="grow min-w-0">
              <span className="block truncate">{personAnzeige(p)}</span>
              <span className={TEXT_GEDAEMPFT_ZEILE}>{p.email}</span>
            </span>
            <span className={TEXT_GEDAEMPFT}>{p.allowed ? "wechseln" : "nur Admin"}</span>
          </button>
        ))}
      </div>
      <div className="mt-2">
        <Knopf groesse="md" variante="sekundaer" volleBreite="immer" symbol={<NutzerPlus />} ariaExpanded={erzeugenOffen} onClick={() => setzeErzeugenOffen((o) => !o)}>
          Test-Nutzer erzeugen
        </Knopf>
      </div>
      {erzeugenOffen ? (
        <div className="mt-2 space-y-2">
          <p className={TEXT_GEDAEMPFT}>Rollen wählen (ohne Administrator). Der Test-Nutzer läuft nach 60 Minuten ab.</p>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {testNutzer.rollen.map((r) => (
              <Kontrollkaestchen
                key={r.key}
                id={`dev-menue-rolle-${r.key}`}
                label={r.label}
                checked={gewaehlt.includes(r.key)}
                onChange={(e) => setzeGewaehlt((alt) => (e.target.checked ? [...alt, r.key] : alt.filter((k) => k !== r.key)))}
              />
            ))}
          </div>
          <Knopf groesse="md" variante="primaer" volleBreite="immer" disabled={gewaehlt.length === 0 || wartet} onClick={erzeuge}>
            Erzeugen und anmelden
          </Knopf>
        </div>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------------------------------
// Plattform

export interface DevMenueVerweis {
  label: string;
  beschreibung: string;
  href: string;
  symbol: "ablauf" | "datenbank" | "ki";
}

export interface DevMenuePlattform {
  /** `AI_ENABLED` (ENV, Ops-Cockpit): der Hauptschalter der KI; nur gelesen, ein eigener Override existiert nicht. */
  aiEnabled: boolean;
  /** Stand der Agenten-Autonomie (Plattform-Einstellung). */
  agentenAutonomie: boolean;
  /** Setzen der Agenten-Autonomie: nur mit dem Recht der Action (`platform:settings.write`); sonst `null` und die Zeile ist nur lesend. */
  autonomieSetzen: ((an: boolean) => Promise<unknown>) | null;
  verweise: readonly DevMenueVerweis[];
}

function VerweisSymbol({ art }: { art: DevMenueVerweis["symbol"] }) {
  if (art === "ablauf") return <Ablauf />;
  if (art === "datenbank") return <Datenbank />;
  return <SparklesSymbol klasse="shrink-0 size-4" />;
}

function PlattformInhalt({ plattform }: { plattform: DevMenuePlattform }) {
  const [autonomie, setzeAutonomie] = useState(plattform.agentenAutonomie);
  const [satz, setzeSatz] = useState<string | null>(null);
  const [wartet, starte] = useTransition();

  function schalte() {
    const setzen = plattform.autonomieSetzen;
    if (!setzen || wartet) return;
    setzeSatz(null);
    const neu = !autonomie;
    starte(async () => {
      try {
        await setzen(neu);
        setzeAutonomie(neu);
      } catch (e) {
        setzeSatz(satzFuerFehler(e, DEV_FEHLSCHLAG_SCHALTER));
      }
    });
  }

  return (
    <div>
      <Abschnittskopf titel="Plattform" geltung="alle" />
      {/* KI-Funktionen: nur Anzeige des Betriebs-Schalters, kein eigener Schalter im Menü (eine Wahrheit, Ops-Cockpit). */}
      <div className="flex justify-between items-start gap-3 py-2 ai-gated">
        <div className="min-w-0">
          <span className="flex items-center gap-x-1.5 text-sm text-foreground">
            <span className="text-ki ai-mark" data-ki="gated">
              <SparklesSymbol klasse="shrink-0 size-3.5" />
            </span>
            KI-Funktionen plattformweit
          </span>
          <p className={TEXT_GEDAEMPFT}>
            {plattform.aiEnabled
              ? "Eingeschaltet (AI_ENABLED). Änderung nur im Ops-Cockpit."
              : "Durch den Betrieb abgeschaltet (AI_ENABLED). Änderung nur im Ops-Cockpit."}
          </p>
        </div>
        <Badge ton={plattform.aiEnabled ? "erfolg" : "gedaempft"}>{plattform.aiEnabled ? "an" : "aus"}</Badge>
      </div>
      {satz ? <Alert ton="warnung">{satz}</Alert> : null}
      {plattform.autonomieSetzen ? (
        <SchalterZeile
          id="dev-menue-agenten-autonomie"
          titel="Agenten-Autonomie"
          hinweis="Autonome Agentenläufe erlauben. Wirkt für alle, wird protokolliert."
          an={autonomie}
          beiUmschalten={schalte}
          gesperrt={wartet}
        />
      ) : (
        <div className="flex justify-between items-start gap-3 py-2">
          <div className="min-w-0">
            <span className="block text-sm text-foreground">Agenten-Autonomie</span>
            <p className={TEXT_GEDAEMPFT}>Autonome Agentenläufe. Ändern darf nur, wer das Recht dazu hat.</p>
          </div>
          <Badge ton={autonomie ? "erfolg" : "gedaempft"}>{autonomie ? "an" : "aus"}</Badge>
        </div>
      )}
      {plattform.verweise.length > 0 ? (
        <div className="mt-1 pt-1 border-t border-dropdown-divider space-y-0.5">
          {plattform.verweise.map((v) => (
            <Link key={v.href} href={v.href} className={MENUE_EINTRAG}>
              <span className="text-muted-foreground-1">
                <VerweisSymbol art={v.symbol} />
              </span>
              <span className="grow min-w-0">
                <span className="block truncate">{v.label}</span>
                <span className={TEXT_GEDAEMPFT_ZEILE}>{v.beschreibung}</span>
              </span>
              <ChevronRechts />
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------------------------------
// Meine Sicht lokal: Zustand in der immer eingehängten Hülle, damit die Wahl nach jedem Laden wieder gilt

interface Ansicht {
  aiMarkerAn: boolean;
  rasterAn: boolean;
  schalteAiMarker: () => void;
  schalteRaster: () => void;
}

function liesMerker(schluessel: string): boolean {
  try {
    return localStorage.getItem(schluessel) === "1";
  } catch {
    return false;
  }
}

function schreibeMerker(schluessel: string, wert: boolean): void {
  try {
    localStorage.setItem(schluessel, wert ? "1" : "0");
  } catch {
    /* Speicher gesperrt (privates Fenster): die Wahl gilt dann nur bis zum Neuladen */
  }
}

/** Zustand des Fensters je Browser: Lesen und Schreiben dürfen scheitern (privates Fenster, gesperrter Speicher), dann gilt die Wahl nur bis zum Neuladen. */
function liesText(schluessel: string): string | null {
  try {
    return localStorage.getItem(schluessel);
  } catch {
    return null;
  }
}

function schreibeText(schluessel: string, wert: string): void {
  try {
    localStorage.setItem(schluessel, wert);
  } catch {
    /* Speicher gesperrt: kein Fehler, der Zustand wird nicht gemerkt */
  }
}

/**
 * KI-Marker und Raster als reine Sitzungs-Einstellungen (localStorage, kein Server-Zustand). Die Wahl bleibt gespeichert,
 * wirkt aber nur in der Entwicklersicht: in der Nutzersicht (`wirkt` false) sind Marker und Raster am `<html>` aus.
 * Die KI-Vorschau ist keine Anzeige-Einstellung mehr, sondern ein Server-Signal (Cookie, `kiVorschau`).
 */
function useAnsicht(wirkt: boolean): Ansicht {
  const [aiMarkerAn, setzeMarker] = useState(false);
  const [rasterAn, setzeRaster] = useState(false);

  useEffect(() => {
    setzeMarker(liesMerker(KEY_AI_MARKER_AN));
    setzeRaster(liesMerker(KEY_RASTER_AN));
  }, []);

  useEffect(() => {
    const wurzel = document.documentElement;
    if (aiMarkerAn && wirkt) wurzel.setAttribute("data-markers", "on");
    else wurzel.removeAttribute("data-markers");
  }, [aiMarkerAn, wirkt]);

  useEffect(() => {
    document.documentElement.classList.toggle("dev-grid", rasterAn && wirkt);
  }, [rasterAn, wirkt]);

  return {
    aiMarkerAn,
    rasterAn,
    schalteAiMarker: () => {
      const neu = !aiMarkerAn;
      setzeMarker(neu);
      schreibeMerker(KEY_AI_MARKER_AN, neu);
    },
    schalteRaster: () => {
      const neu = !rasterAn;
      setzeRaster(neu);
      schreibeMerker(KEY_RASTER_AN, neu);
    },
  };
}

// ------------------------------------------------------------------------------------------
// Hülle

export interface DevMenueKiVorschauProps {
  aus: boolean;
  /** Server-Action; wirft bei Ablehnung den festen Satz der Dev-Werkzeuge. */
  setzen: (eingabe: { aus: boolean }) => Promise<unknown>;
}

/** Zustand der KI-Vorschau im Menü (Wert + Umschalter). */
interface DevMenueKiVorschau {
  aus: boolean;
  umschalten: () => void;
}

export interface DevMenueProps {
  /** Text der Umgebung in der Pille (DEV, STAGE, …). */
  umgebungLabel: string;
  /** Stage trägt die Warn-Pille, alles andere die Primär-Pille. */
  istStage: boolean;
  /** Name der dargestellten Person bei laufender Impersonation, sonst `null`. */
  impersoniertAls: string | null;
  /** Build-Angaben (alle optional, wenn der Build sie nicht trägt); `gebaut` ist fertig formatiert. */
  build: { branch: string | null; sha: string | null; gebaut: string | null };
  /** Gespeicherte Sicht der Person und der Endpunkt, der sie speichert (`/api/user-prefs/sidebar`). */
  sicht: DevSicht;
  sichtEndpunkt: string;
  /** `AI_ENABLED`: ist die KI im Betrieb aus, ist der Schalter KI-Vorschau gesperrt (es gibt nichts zu verengen). */
  aiEnabled: boolean;
  /**
   * KI-Vorschau „aus“ (K-C): Stand des Server-Signals und die Server-Action, die es setzt. `null` = der Schalter fehlt
   * (Stage: das Signal wird dort nie ausgewertet).
   */
  kiVorschau: DevMenueKiVorschauProps | null;
  personen: DevMenuePersonen;
  testNutzer: DevMenueTestNutzer;
  /** Reiter Plattform (nur dev/verify); `null` = der Reiter fehlt still. */
  plattform: DevMenuePlattform | null;
  /** Ziel „Alle Dev-Seiten“ (nur wo die Person den DEV-Bereich sehen darf); `null` = der Link entfällt. */
  devSeitenHref: string | null;
  /** Nur für Tests: ersetzt das Neuladen der Seite nach einem Wechsel der Ansicht (der Server reicht nie eine Funktion außer Server-Actions durch). */
  neuLaden?: () => void;
}

function Kopf({ p, beiSchliessen }: { p: DevMenueProps; beiSchliessen: () => void }) {
  const [ort, setzeOrt] = useState("");
  useEffect(() => {
    setzeOrt(`${Intl.DateTimeFormat().resolvedOptions().timeZone} · ${navigator.language}`);
  }, []);
  const build = [p.build.branch, p.build.sha].filter(Boolean).join(" · ");
  return (
    <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-3 border-b border-line-2">
      <div className="min-w-0">
        <div className="flex items-center gap-x-2 mb-2">
          <span className="font-medium text-foreground">Entwicklung</span>
          <Badge ton="gedaempft">nur {p.istStage ? "Stage" : "Dev"}</Badge>
        </div>
        <div className={KOPF_ZEILE}>
          <Badge ton={p.istStage ? "warnung" : "primaer"}>{p.umgebungLabel}</Badge>
          {build ? <span>{build}</span> : null}
          {p.build.gebaut ? (
            <>
              <span aria-hidden="true">·</span>
              <span>gebaut {p.build.gebaut}</span>
            </>
          ) : null}
          {ort ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{ort}</span>
            </>
          ) : null}
        </div>
      </div>
      <Knopf nurSymbol variante="ghost" groesse="sm" label="Schließen" symbol={<Schliessen />} onClick={beiSchliessen} />
    </div>
  );
}

export function DevMenue(p: DevMenueProps) {
  const pille = useRef<HTMLButtonElement>(null);
  const fenster = useRef<HTMLDivElement>(null);
  const fokusBeimOeffnen = useRef(false);
  const basisId = useId();
  const [offen, setzeOffen] = useState(false);
  const router = useRouter();
  const [wartet, starte] = useTransition();
  const [sicht, setzeSicht] = useState<DevSicht>(p.sicht);
  const [satz, setzeSatz] = useState<string | null>(null);
  const [kiAus, setzeKiAus] = useState(p.kiVorschau?.aus ?? false);
  const ansicht = useAnsicht(sicht === "entwickler");
  const [aktiverReiter, setzeAktiverReiter] = useState("sicht");

  // Der Server ist die Quelle (nach `router.refresh()` kommen neue Props).
  useEffect(() => setzeSicht(p.sicht), [p.sicht]);
  useEffect(() => setzeKiAus(p.kiVorschau?.aus ?? false), [p.kiVorschau?.aus]);

  function wechsle(neu: DevSicht) {
    if (neu === sicht || wartet) return;
    setzeSatz(null);
    const vorher = sicht;
    setzeSicht(neu);
    starte(async () => {
      try {
        // Die Nutzersicht blendet alle Hilfen aus: eine laufende KI-Vorschau endet dabei (sonst ohne Streifen unerklärt).
        if (neu === "nutzer" && kiAus && p.kiVorschau) {
          await p.kiVorschau.setzen({ aus: false });
          setzeKiAus(false);
        }
        const antwort = await fetch(p.sichtEndpunkt, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ devMode: gespeichertAusSicht(neu) }),
          keepalive: true,
        });
        if (!antwort.ok) throw new Error("fehlgeschlagen");
        router.refresh();
      } catch (fehler) {
        setzeSicht(vorher);
        setzeSatz(satzFuerFehler(fehler, DEV_FEHLSCHLAG_SCHALTER));
      }
    });
  }

  const kiVorschau: DevMenueKiVorschau | null = p.kiVorschau
    ? {
        aus: kiAus,
        umschalten: () => {
          if (wartet) return;
          setzeSatz(null);
          const neu = !kiAus;
          setzeKiAus(neu);
          starte(async () => {
            try {
              await p.kiVorschau!.setzen({ aus: neu });
              router.refresh();
            } catch (fehler) {
              setzeKiAus(!neu);
              setzeSatz(satzFuerFehler(fehler, DEV_FEHLSCHLAG_SCHALTER));
            }
          });
        },
      }
    : null;

  // Strg+Umschalt+D öffnet und schließt das Fenster (derselbe Klick wie am Auslöser).
  useEffect(() => {
    const beiTaste = (e: KeyboardEvent) => {
      if (!istMenueKuerzel(e)) return;
      e.preventDefault();
      pille.current?.click();
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, []);

  // Gemerkter Zustand dieses Browsers (nach dem Einhängen, damit Server und Client dasselbe zeichnen): offen und letzter Reiter.
  useEffect(() => {
    if (liesText(KEY_FENSTER_OFFEN) === "1") setzeOffen(true);
    const gemerkt = liesText(KEY_FENSTER_REITER);
    if (gemerkt) setzeAktiverReiter(gemerkt);
  }, []);

  // Esc schließt das Fenster, wenn der Fokus darin oder am Auslöser liegt (ein offenes Modal darüber behält sein eigenes Esc).
  useEffect(() => {
    if (!offen) return;
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const aktiv = document.activeElement;
      const imFenster = !!fenster.current && !!aktiv && fenster.current.contains(aktiv);
      if (!imFenster && aktiv !== pille.current && aktiv !== document.body) return;
      setzeOffenGemerkt(false);
      if (imFenster) pille.current?.focus();
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, [offen]);

  // Beim Öffnen durch eine Bedienung (nicht beim Wiederherstellen nach dem Laden) startet der Fokus am gewählten Reiter.
  useEffect(() => {
    if (!offen || !fokusBeimOeffnen.current) return;
    fokusBeimOeffnen.current = false;
    const ziel = fenster.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]') ?? fenster.current?.querySelector<HTMLElement>("button");
    ziel?.focus({ preventScroll: true });
  }, [offen]);

  function setzeOffenGemerkt(neu: boolean) {
    setzeOffen(neu);
    schreibeText(KEY_FENSTER_OFFEN, neu ? "1" : "0");
  }

  function waehleReiter(id: string) {
    setzeAktiverReiter(id);
    schreibeText(KEY_FENSTER_REITER, id);
  }

  const reiter: DevReiter[] = [
    {
      id: "sicht",
      label: "Meine Sicht",
      gruppe: "Sitzung",
      symbol: <Auge />,
      inhalt: <MeineSichtInhalt sicht={sicht} wechsle={wechsle} wartet={wartet} satz={satz} ansicht={ansicht} kiVorschau={kiVorschau} aiEnabled={p.aiEnabled} />,
    },
    {
      id: "ansicht-als",
      label: "Ansicht als",
      gruppe: "Sitzung",
      symbol: <Personen />,
      inhalt: <AnsichtAlsInhalt aktiv={offen && aktiverReiter === "ansicht-als"} personen={p.personen} testNutzer={p.testNutzer} impersoniertAls={p.impersoniertAls} neuLaden={p.neuLaden ?? (() => window.location.reload())} />,
    },
  ];
  if (p.plattform) reiter.push({ id: "plattform", label: "Plattform", gruppe: "Plattform", symbol: <Server />, inhalt: <PlattformInhalt plattform={p.plattform} /> });
  // Ein gemerkter Reiter, den es hier nicht gibt (z. B. Plattform auf Stage), fällt auf den ersten zurück.
  const gewaehlt = reiter.some((r) => r.id === aktiverReiter) ? aktiverReiter : (reiter[0]?.id ?? "sicht");

  const text = pillenText(p.umgebungLabel, p.impersoniertAls);
  const fensterId = `${basisId}-fenster`;

  function umschalten() {
    if (!offen) fokusBeimOeffnen.current = true;
    setzeOffenGemerkt(!offen);
  }

  const schliesse = () => {
    setzeOffenGemerkt(false);
    pille.current?.focus();
  };

  return (
    <span className="inline-flex">
      {/* Preline: productivity/productivity-sub-calendars-scheduling/productivity-cat-calendar-drawers/create-event-drawer-with-schedule-form (Seitenleiste rechts, verbreitert auf 760 px, ohne Backdrop; AW-086) */}
      {/* Preline: application-pages/application-pages-sub-profiles-accounts/application-pages-cat-settings-modals/call-settings-modal-with-sidebar-navigation-and-device-controls (Reiterspalte links in Gruppen) */}
      <button
        ref={pille}
        type="button"
        className={p.istStage ? PILLE_WARNUNG : PILLE_PRIMAER}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-controls={offen ? fensterId : undefined}
        aria-label={p.impersoniertAls ? `Umgebung ${p.umgebungLabel}, ansehen als ${p.impersoniertAls}, Entwicklungswerkzeuge öffnen` : `Umgebung ${p.umgebungLabel}, Entwicklungswerkzeuge öffnen`}
        onClick={umschalten}
      >
        <Kolben />
        <span className="max-w-32 sm:max-w-48 truncate">{text}</span>
        <ChevronUnten />
      </button>
      {offen &&
        createPortal(
          sicht === "nutzer" ? (
            // Nutzersicht: das Fenster schrumpft auf den Umschalter, mit dem man zurückschaltet. Kein Kopf, keine Reiter, kein Link.
            <div ref={fenster} id={fensterId} role="dialog" aria-label="Entwicklung" className={FENSTER_KARTE}>
              <SichtUmschalter sicht={sicht} wechsle={wechsle} wartet={wartet} satz={satz} />
            </div>
          ) : (
            <div ref={fenster} id={fensterId} role="dialog" aria-label="Entwicklung" className={FENSTER_FLAECHE}>
              <Kopf p={p} beiSchliessen={schliesse} />
              <ReiterSpalteUndInhalt reiter={reiter} gewaehlt={gewaehlt} beimWaehlen={waehleReiter} />
              <div className="px-4 py-2 border-t border-line-2 flex items-center justify-between gap-2">
                <span className={TEXT_GEDAEMPFT}>Strg + Umschalt + D</span>
                {p.devSeitenHref ? (
                  <Link
                    href={p.devSeitenHref}
                    className="inline-flex items-center gap-x-1.5 text-xs font-semibold text-primary hover:text-primary-hover focus:outline-hidden focus:text-primary-focus"
                    onClick={schliesse}
                  >
                    Alle Dev-Seiten
                    <Extern />
                  </Link>
                ) : null}
              </div>
            </div>
          ),
          document.body,
        )}
    </span>
  );
}

// ------------------------------------------------------------------------------------------
// Reiterspalte (Gruppen) und Inhalt

interface DevReiter {
  id: string;
  label: string;
  gruppe: (typeof GRUPPEN)[number];
  symbol: React.ReactNode;
  inhalt: React.ReactNode;
}

/** Reihenfolge der Gruppen (Spec 5.1/5.4); eine Gruppe ohne Reiter erscheint nicht (keine toten Einträge für künftige Funktionen). */
const GRUPPEN = ["Sitzung", "Prüfen", "Rückmeldung", "Plattform"] as const;

/**
 * Reiterspalte links (ab sm senkrecht mit Gruppenüberschriften, darunter waagerecht scrollend oben) und Inhalt rechts mit eigenem
 * Scroll. Der Zustand liegt in React; Pfeiltasten (beide Richtungen), Pos1 und Ende wechseln den Reiter, der Fokus folgt (roving tabindex).
 */
function ReiterSpalteUndInhalt({ reiter, gewaehlt, beimWaehlen }: { reiter: DevReiter[]; gewaehlt: string; beimWaehlen: (id: string) => void }) {
  const basis = useId();
  const knoepfe = useRef(new Map<string, HTMLButtonElement>());
  const tabId = (id: string) => `${basis}-tab-${id}`;
  const panelId = (id: string) => `${basis}-panel-${id}`;

  const taste = (e: ReactTaste<HTMLButtonElement>, index: number) => {
    const n = reiter.length;
    let ziel: number | null = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") ziel = (index + 1) % n;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") ziel = (index - 1 + n) % n;
    else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = n - 1;
    if (ziel === null) return;
    e.preventDefault();
    const id = reiter[ziel]!.id;
    beimWaehlen(id);
    knoepfe.current.get(id)?.focus();
    knoepfe.current.get(id)?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  };

  return (
    <div className="flex flex-col sm:flex-row grow min-h-0">
      <div role="tablist" aria-label="Werkzeuge" className={REITER_SPALTE}>
        {GRUPPEN.map((gruppe) => {
          const inGruppe = reiter.filter((r) => r.gruppe === gruppe);
          if (inGruppe.length === 0) return null;
          return (
            <div key={gruppe} className="flex sm:flex-col gap-x-1 gap-y-0.5">
              <span aria-hidden="true" className={REITER_GRUPPE_KOPF}>
                {gruppe}
              </span>
              {inGruppe.map((r) => (
                <button
                  key={r.id}
                  ref={(el) => {
                    if (el) knoepfe.current.set(r.id, el);
                    else knoepfe.current.delete(r.id);
                  }}
                  type="button"
                  role="tab"
                  id={tabId(r.id)}
                  aria-selected={gewaehlt === r.id}
                  aria-controls={panelId(r.id)}
                  tabIndex={gewaehlt === r.id ? 0 : -1}
                  className={gewaehlt === r.id ? REITER_AKTIV : REITER_INAKTIV}
                  onClick={() => beimWaehlen(r.id)}
                  onKeyDown={(e) => taste(e, reiter.indexOf(r))}
                >
                  {r.symbol}
                  {r.label}
                </button>
              ))}
            </div>
          );
        })}
      </div>
      <div className="grow min-w-0 overflow-y-auto">
        {reiter.map((r) => (
          <div key={r.id} role="tabpanel" id={panelId(r.id)} aria-labelledby={tabId(r.id)} tabIndex={0} hidden={gewaehlt !== r.id} className="p-4 sm:p-6 focus:outline-hidden">
            {r.inhalt}
          </div>
        ))}
      </div>
    </div>
  );
}
